import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function ResetPassword() {
    const { t } = useLang();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (!token) {
        return (
            <div className="min-h-[80vh] max-w-md mx-auto px-5 py-20" data-testid="reset-missing-token">
                <p className="text-xs tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-4">{t("auth.resetEyebrow")}</p>
                <h1 className="font-display text-5xl uppercase leading-none">{t("auth.resetTitle")}</h1>
                <p className="text-zinc-400 mt-4">{t("auth.resetMissing")}</p>
                <Link to="/forgot-password" className="mt-6 inline-block text-abnormal-acid uppercase tracking-wider font-bold">← {t("auth.forgot")}</Link>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (password.length < 6) { setErr(t("auth.passwordMin")); return; }
        if (password !== confirm) { setErr(t("common.passwordsMismatch")); return; }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success(t("auth.resetOk"));
            nav("/login");
        } catch (e) {
            setErr(e.response?.data?.detail || t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="reset-page" className="min-h-[80vh] max-w-md mx-auto px-5 py-20">
            <p className="text-xs tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-4">{t("auth.resetEyebrow")}</p>
            <h1 className="font-display text-5xl uppercase leading-none">{t("auth.resetTitle")}</h1>
            <p className="text-zinc-400 mt-4">{t("auth.resetSub")}</p>

            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="reset-form">
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{t("auth.passwordMin")}</label>
                    <input
                        data-testid="reset-password"
                        type="password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 w-full bg-abnormal-charcoal border border-white/10 focus:border-abnormal-acid outline-none px-4 py-3"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{t("common.confirmPassword")}</label>
                    <input
                        data-testid="reset-confirm"
                        type="password"
                        required
                        minLength={6}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="mt-1 w-full bg-abnormal-charcoal border border-white/10 focus:border-abnormal-acid outline-none px-4 py-3"
                    />
                </div>
                {err && <p data-testid="reset-error" className="text-sm text-abnormal-acid">{err}</p>}
                <button
                    data-testid="reset-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full bg-abnormal-acid text-black uppercase tracking-wider font-bold py-3 hover:bg-abnormal-paper hover:text-black transition-colors disabled:opacity-60"
                >
                    {busy ? t("auth.loginBusy") : t("auth.resetBtn")}
                </button>
            </form>
        </div>
    );
}
